import { Component, EventEmitter, forwardRef, Input, OnChanges, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FileService, TypedChanges } from '@platform-frontend/util';
import { ImageFileType } from '../image-file-type';

@Component({
  selector: 'yat-small-image-select',
  templateUrl: './small-image-select.component.html',
  styleUrls: ['./small-image-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SmallImageSelectComponent),
      multi: true
    }
  ]
})
export class SmallImageSelectComponent implements ControlValueAccessor, OnChanges {
  constructor(private fileService: FileService) {}

  /** maximum file size in mb */
  @Input()
  maxFileSize: number = 1;

  @Input()
  fileTypes: ImageFileType[] = [];

  @Input()
  previewOnly: boolean = false;

  @Input()
  size: 'small' | 'normal' = 'normal';

  @Input()
  displayWide: boolean = false;

  @Output()
  imageUpdated: EventEmitter<void> = new EventEmitter();

  @Output()
  imageDropped: EventEmitter<void> = new EventEmitter();

  @Output()
  imageDragover: EventEmitter<void> = new EventEmitter();

  @Output()
  imageDragleave: EventEmitter<void> = new EventEmitter();

  selectedImage: string | null;

  fileExtensions: string[] = [];

  warning: null | 'wrongFileType' | 'maxFileSizeExceeded' = null;

  isDragged: boolean = false;

  private timeout: ReturnType<typeof setTimeout> | null = null;

  filename: string;

  onTouched: () => void;

  onChange: (newSelectedImage: string | null) => void;

  writeValue(newSelectedImage: string | null): void {
    this.selectedImage = newSelectedImage;
  }

  registerOnChange(onChangeCallback: (newSelectedImage: string | null) => void): void {
    this.onChange = onChangeCallback;
  }

  registerOnTouched(onTouchedCallback: () => void): void {
    this.onTouched = onTouchedCallback;
  }

  ngOnChanges(changes: TypedChanges<SmallImageSelectComponent>): void {
    if (changes.fileTypes) {
      this.fileExtensions = this.fileTypes.slice();
      if (this.fileTypes.includes('jpg')) {
        this.fileExtensions.push('jpeg');
      }
    }
  }

  onDragOver(event: DragEvent): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.timeout !== null) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
    this.isDragged = true;
    this.imageDragover.emit();
  }

  onDragLeave(event: DragEvent): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.timeout === null) {
      this.timeout = setTimeout(() => (this.isDragged = false), 100);
    }
    this.imageDragleave.emit();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragged = false;
    const { dataTransfer } = event;
    if (dataTransfer) {
      if (dataTransfer.items) {
        if (dataTransfer.items[0].kind === 'file') {
          this.selectFile(dataTransfer.items[0].getAsFile());
        }
        dataTransfer.items.clear();
      } else if (dataTransfer.files) {
        this.selectFile(dataTransfer.files[0]);
        dataTransfer.clearData();
      }
      this.imageDropped.emit();
    }
  }

  selectFileFromEventTarget(eventTarget: EventTarget | null): void {
    const fileList: FileList | null = eventTarget !== null ? (eventTarget as HTMLInputElement).files : null;
    const newSelectedFile: File | null = fileList ? fileList[0] : null;
    this.selectFile(newSelectedFile);
  }

  private selectFile(newSelectedFile: File | null): void {
    if (newSelectedFile !== null && !this.fileExtensions.includes(newSelectedFile.name.split('.').slice(-1)[0].toLowerCase())) {
      this.warning = 'wrongFileType';
    } else if (newSelectedFile !== null && newSelectedFile.size > this.maxFileSize * 1024 * 1024) {
      this.warning = 'maxFileSizeExceeded';
    } else {
      this.warning = null;
    }

    if (this.warning) {
      this.selectedImage = '';
      this.onChange(this.selectedImage);
    } else if (newSelectedFile !== null) {
      this.filename = newSelectedFile.name;
      this.fileService.encodeAsBase64(newSelectedFile, false).subscribe(base64 => {
        this.selectedImage = base64;
        this.onChange(this.selectedImage);
        this.onTouched();
        if (this.imageUpdated) {
          this.imageUpdated.emit();
        }
      });
    }
  }
}
